import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';
import { useLanguage } from '../context/language';

const buildAbsoluteUrl = (siteUrl, path = '') => {
  if (!path) {
    return siteUrl;
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${siteUrl}${normalizedPath}`;
};

const normalizePathname = pathname => {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.replace(/\/+$/, '');
};

const buildAlternateLanguageLinks = (siteUrl, pathname) => {
  const normalizedPath = normalizePathname(pathname);
  let suffix = null;

  if (normalizedPath === '/en' || normalizedPath === '/fr') {
    suffix = '';
  } else if (normalizedPath.startsWith('/en/')) {
    suffix = normalizedPath.slice(3);
  } else if (normalizedPath.startsWith('/fr/')) {
    suffix = normalizedPath.slice(3);
  }

  if (suffix === null) {
    return [];
  }

  const enPath = suffix ? `/en${suffix}` : '/en';
  const frPath = suffix ? `/fr${suffix}` : '/fr';

  return [
    { hrefLang: 'en', href: buildAbsoluteUrl(siteUrl, enPath) },
    { hrefLang: 'fr', href: buildAbsoluteUrl(siteUrl, frPath) },
    { hrefLang: 'x-default', href: buildAbsoluteUrl(siteUrl, '/') },
  ];
};

const Head = ({ title, description, image, noindex, pathname: pathnameProp, schemaNodes }) => {
  const { language, content } = useLanguage();
  const localizedHead = content.head || {};
  const { pathname: locationPathname } = useLocation();
  const pathname = normalizePathname(pathnameProp || locationPathname || '/');

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            defaultTitle: title
            defaultDescription: description
            siteUrl
            defaultImage: image
            twitterUsername
            siteLanguage
            siteLocale
            author
            jobTitle
            email
            themeColor
            keywords
            social {
              linkedin
            }
            location {
              city
              country
              countryCode
            }
          }
        }
      }
    `,
  );

  const {
    defaultTitle,
    defaultDescription,
    siteUrl,
    defaultImage,
    twitterUsername,
    siteLanguage,
    siteLocale,
    author,
    jobTitle,
    email,
    themeColor,
    keywords,
    social,
    location,
  } = site.siteMetadata;
  const keywordList =
    localizedHead.keywords && localizedHead.keywords.length > 0
      ? localizedHead.keywords
      : keywords || [];
  const effectiveLanguage = language || siteLanguage || 'en';
  const effectiveLocale =
    localizedHead.locale || siteLocale || (effectiveLanguage === 'fr' ? 'fr_FR' : 'en_US');
  const cleanSiteUrl = siteUrl.replace(/\/$/, '');
  const imageUrl = buildAbsoluteUrl(cleanSiteUrl, image || defaultImage);
  const canonicalUrl = buildAbsoluteUrl(cleanSiteUrl, pathname);
  const alternateLanguageLinks = buildAlternateLanguageLinks(cleanSiteUrl, pathname);

  const noindexPathPatterns = [/^\/404/, /^\/archive\/?$/, /^\/pensieve(\/|$)/];
  const shouldNoindex = noindex || noindexPathPatterns.some(pattern => pattern.test(pathname));
  const robotsContent = shouldNoindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  const seo = {
    title: title || localizedHead.title || defaultTitle,
    description: description || localizedHead.description || defaultDescription,
    image: imageUrl,
    url: canonicalUrl,
  };

  const personId = `${cleanSiteUrl}#person`;
  const websiteId = `${cleanSiteUrl}#website`;
  const webpageId = `${canonicalUrl}#webpage`;
  const sameAs = [social?.linkedin].filter(Boolean);

  const personSchema = {
    '@type': 'Person',
    '@id': personId,
    name: author || defaultTitle,
    url: cleanSiteUrl,
    image: seo.image,
    ...(email && { email: `mailto:${email}` }),
    ...(jobTitle && { jobTitle }),
    ...(sameAs.length > 0 && { sameAs }),
    ...(keywordList.length > 0 && { knowsAbout: keywordList }),
    ...((location?.city || location?.country || location?.countryCode) && {
      address: {
        '@type': 'PostalAddress',
        ...(location?.city && { addressLocality: location.city }),
        ...(location?.countryCode
          ? { addressCountry: location.countryCode }
          : location?.country
            ? { addressCountry: location.country }
            : {}),
      },
    }),
  };

  const websiteSchema = {
    '@type': 'WebSite',
    '@id': websiteId,
    url: cleanSiteUrl,
    name: localizedHead.title || defaultTitle,
    description: localizedHead.description || defaultDescription,
    inLanguage: effectiveLanguage,
    publisher: {
      '@id': personId,
    },
  };

  const webpageSchema = {
    '@type': pathname === '/' ? 'ProfilePage' : 'WebPage',
    '@id': webpageId,
    url: canonicalUrl,
    name: seo.title,
    description: seo.description,
    inLanguage: effectiveLanguage,
    isPartOf: {
      '@id': websiteId,
    },
    about: {
      '@id': personId,
    },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: seo.image,
    },
    ...(pathname === '/' && {
      mainEntity: {
        '@id': personId,
      },
    }),
  };

  const normalizedSchemaNodes = Array.isArray(schemaNodes) ? schemaNodes.filter(Boolean) : [];

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [websiteSchema, personSchema, webpageSchema, ...normalizedSchemaNodes],
  };

  return (
    <Helmet title={seo.title}>
      <html lang={effectiveLanguage} />

      <meta name="description" content={seo.description} />
      <meta name="keywords" content={keywordList.join(', ')} />
      <meta name="author" content={author || defaultTitle} />
      <meta name="image" content={seo.image} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="theme-color" content={themeColor || '#0c1529'} />

      <link rel="canonical" href={canonicalUrl} />
      {alternateLanguageLinks.map(link => (
        <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
      ))}
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.webmanifest" />

      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:alt" content={seo.title} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={defaultTitle} />
      <meta property="og:locale" content={effectiveLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={twitterUsername} />
      <meta name="twitter:site" content={twitterUsername} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:url" content={seo.url} />

      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
};

export default Head;

Head.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  noindex: PropTypes.bool,
  pathname: PropTypes.string,
  schemaNodes: PropTypes.arrayOf(PropTypes.object),
};

Head.defaultProps = {
  title: null,
  description: null,
  image: null,
  noindex: false,
  pathname: null,
  schemaNodes: null,
};
